<template>
  <div class="breakdown-wrapper" :style="hasOrganizationUnits ? 'display: grid; grid-template-columns: 5fr 3fr;' : ''">
    <div>
      <div class="breakdown-header">
        <div class="section-header">
          <QuestionBadge :id="'pendo-statement-calculation-breakdown'">
            <h2>CALCULATION BREAKDOWN</h2>
          </QuestionBadge>
        </div>
        <div class="company-performance">
          <span class="label">Company Performance</span>
          <span>{{ client.performance.position.label }}</span>
        </div>
        <div class="plan-funding">
          <span class="label">Potential Award</span>
          <span class="value">{{ formatCurrency(organization.totals.funding) }}</span>
        </div>
      </div>

      <div class="breakdown-content">
        <slot name="content">
          <organization-units
            v-if="hasOrganizationUnits"
            :client="client"
            :employee="employee"
            :organization="organization"
          />
          <individual-modifier
            v-if="showIndividualGoals"
            :tbd="goalsTBD"
            :message="employee.performance.goals.performance_label"
            :value="employee.performance.goals.performance_total"
          />
          <individual-modifier
            v-if="showIndividualRating"
            :tbd="ratingTBD"
            :message="ratingMessage()"
            :value="employee.performance.rating.achieved_value"
          />
        </slot>
      </div>
      <div class="breakdown-footer">
        <slot name="footer">
          <div class="forecasted-bonus-wrap">
            <div class="bonus">
              <span class="label">{{ forecast.label }} Award</span>
              <span
                v-if="forecast.adjustment !== 0"
                class="value"
              >{{ formatCurrency(forecast.modified) }}</span>
              <span
                v-else
                class="value"
              >{{ formatCurrency(forecast.adjusted) }}</span>
            </div>
            <div
              v-if="forecast.tweaked_percent !== 0"
              class="adjustments-wrap"
            >
              <span class="label">Employer Adjustment</span>
              <span class="value">{{ forecast.tweaked_percent < 0 ? '-' : '+' }}{{ formatPercent(forecast.tweaked_percent) }}</span>
            </div>
            <div
              v-if="forecast.adjustment !== 0"
              class="adjustments-wrap"
            >
              <span class="label">Employer Adjustment</span>
              <span class="value">{{ forecast.adjustment < 0 ? '-' : '+' }}{{ formatCurrency(forecast.adjustment) }}</span>
            </div>
          </div>
          <div
            v-if="forecast.adjustment !== 0"
            class="total-award"
          >
            <span class="label">Total Award</span>
            <span class="value">{{ formatCurrency(forecast.adjusted) }}</span>
          </div>
        </slot>
      </div>
    </div>
    <div
      v-if="hasOrganizationUnits"
      class="allocation"
    >
      <h2>ALLOCATION</h2>
      <p style="font-size: 18px;">Your award is allocated on the following plan metrics. See Metrics/Performance below for additional information.</p>
      <doughnut-chart
        class="pie-chart"
        :client="client"
        :employee="employee"
        :organization="organization"
        :distributions="distributions"
      />
    </div>
  </div>
</template>

<script>
import DoughnutChart from "@/components/plans/short-term/charts/DoughnutChart.vue";
import IndividualModifier from "@/components/plans/short-term/calculation/IndividualModifier.vue";
import OrganizationUnits from "@/components/plans/short-term/calculation/OrganizationUnits.vue";
import QuestionBadge from "@/components/pendo/QuestionBadge.vue";
import VLcurrency from "@/composables/VLcurrency";
import VLpercent from "@/composables/VLpercent";

export default {
    name: "PlanBreakdown",

    components: {
        QuestionBadge,
        OrganizationUnits,
        IndividualModifier,
        DoughnutChart,
    },

    props: {
        forecast: {
            type: Object,
            required: true,
        },
        client: {
            type: Object,
            required: true,
        },
        employee: {
            type: Object,
            required: true
        },
        organization: {
            type: Object,
            required: false,
        },
        distributions: {
          type: Array,
          required: true,
        },
    },

    data() {
        return {}
    },

    computed: {
        showIndividualGoals() {
            return this.employee.performance?.goals && this.employee.performance?.mode === "as-modifier"
        },
        showIndividualRating() {
            return this.employee.performance?.rating && this.employee.performance?.mode === "as-modifier"
        },
        hasOrganizationUnits() {
            return this.organization?.units && Object.values(this.organization?.units).length
        },
        ratingTBD() {
            return this.employee.performance.rating.achieved_label === null || this.employee.performance.rating.achieved_label === ""
        },
        goalsTBD() {
            return this.employee.performance.goals.tbd
        },
    },

    methods: {
        formatCurrency(value, decimals = 0, currency = 'USD') {
            return VLcurrency.formatCurrency(value, decimals, currency);
        },
        formatPercent(value, decimals = 0) {
            return VLpercent.formatPercent(value, decimals);
        },
        ratingMessage() {
            if (this.employee.performance.rating.achieved_label) return `${this.employee.performance.rating.performance_label} - ${this.employee.performance.rating.achieved_label}`
            return `${this.employee.performance.rating.performance_label} - TBD`
        },
    },

}
</script>

<style lang="scss" scoped>
@use "../../../../scss/_variables.scss";

.section-header {
    margin-bottom: 0.5rem;
}

.label {
    font-family: variables.$font-primary;
    font-size: variables.$text-base;
}

.value {
    font-size: variables.$text-md;
}

.breakdown-wrapper {
  @media screen and (max-width: 1000px) {
    display: flex !important;
    flex-direction: column-reverse !important;
    .allocation {
      margin-left: 0px;
    }
  }
}

.breakdown-header {
    display: flex;
    flex-direction: column;

    padding-top: 2.4rem;
    padding-bottom: 1.4rem;

    .company-performance {
        display: flex;
        justify-content: space-between;

        margin-bottom: 0.4rem;
    }

    .plan-funding {
        display: flex;
        justify-content: space-between;

        .value {
            color: var(--highlight-color);
            font-size: variables.$text-base;
            font-family: variables.$font-secondary;
            font-weight: 800;
        }
    }
}

.breakdown-footer {

    .forecasted-bonus-wrap {
        padding-block: 0.5rem;

        .bonus {
            display: flex;
            justify-content: space-between;
            align-items: center;

            .value {
                color: var(--highlight-color);
                font-size: variables.$text-lg;
                font-family: variables.$font-secondary;
                font-weight: 800;
            }
        }

        .adjustments-wrap {
            display: flex;
            flex-direction: column;
            align-items: flex-end;

            .adjustments {
                display: grid;
                grid-template-columns: min-content min-content;
                grid-template-rows: min-content;

                grid-column-gap: 2rem;

                justify-items: flex-end;
                align-items: center;

                .label {
                    padding-block: 0.4rem;

                    font-size: variables.$text-sm;
                    font-weight: normal;
                    font-family: variables.$font-primary;
                    color: variables.$charcoal-tint-50;
                }

                .value {
                    color: var(--highlight-color);
                    font-size: variables.$text-base;
                    font-family: variables.$font-secondary;
                    font-weight: 800;
                }
            }
        }
    }

    .total-award {
        display: flex;
        justify-content: space-between;
        align-items: center;

        padding-block: 0.5rem;

        border-top: 2px solid variables.$cloud;

        .value {
            color: var(--highlight-color);
            font-size: variables.$text-lg;
            font-family: variables.$font-secondary;
            font-weight: 800;
        }
    }
}

.allocation {
  padding-top: 2.4rem;
  margin-left: 20px;
  .pie-chart {
    max-width: 300px;
  }
}
</style>